import { useMutation } from '@tanstack/react-query';
import React from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage } from 'react-intl';

import { bulkEditItems } from 'apis/restaurant-api/merchant-admin/menu2/api';
import { BulkActionBasePayload, Category, MenuContainer } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { ConfirmModal } from 'common/components';
import { reportBulkAction } from 'modules/menu/behavioral';
import { useBulkChangeEnableStatusTracking } from 'modules/menu/hooks/telemetryHooks';
import useMenuData from 'modules/menu/useMenuData';

import { buildBulkActionBasePayload } from '../../utils';
import BulkActionModalTitle from '../BulkActionModalTitle';

type Props = {
  categories: Category[];
  menuId: string;
  onClose: () => void;
  onClearSelection?: () => void;
  onSubmitStart?: () => void;
  selectedIds: Set<string>;
  shownModal: 'disable' | 'enable';
};

type BulkStatusChangePayload = BulkActionBasePayload & {
  enabled_flag: boolean;
};

const BulkStatusChangeModal = ({
  categories,
  menuId,
  onClose,
  onClearSelection,
  onSubmitStart,
  selectedIds,
  shownModal,
}: Props) => {
  const { updateMenuData } = useMenuData();
  const trackChangeStatus = useBulkChangeEnableStatusTracking([...selectedIds]);

  const handleStatusChange = useMutation((payload: BulkStatusChangePayload) => bulkEditItems(menuId, payload), {
    onMutate: (variables: BulkStatusChangePayload) => {
      if (onSubmitStart) {
        onSubmitStart();
      } else {
        reportBulkAction(
          selectedIds.size,
          variables.enabled_flag === true ? 'BULK_ENABLE_ITEMS' : 'BULK_DISABLE_ITEMS',
        );
      }
    },
    onSuccess: (data: MenuContainer, variables: BulkStatusChangePayload) => {
      updateMenuData({ actionType: 'updateMenuContainer', payload: data });
      if (onClearSelection) {
        onClearSelection();
      }
      const descriptionId =
        variables.enabled_flag === true
          ? 'menu.bulkActions.statusChange.enableItemsSuccessDescription'
          : 'menu.bulkActions.statusChange.disableItemsSuccessDescription';
      trackChangeStatus(variables.enabled_flag);

      toast.success(<FormattedMessage id={descriptionId} values={{ count: selectedIds.size }} />);
    },
    onError: (variables: BulkStatusChangePayload) => {
      const descriptionId =
        variables.enabled_flag === true
          ? 'menu.bulkActions.statusChange.enableItemsErrorDescription'
          : 'menu.bulkActions.statusChange.disableItemsErrorDescription';

      toast.error(<FormattedMessage id={descriptionId} values={{ count: selectedIds.size }} />, { duration: Infinity });
    },
    onSettled: () => {
      onClose();
    },
  });
  return shownModal === 'enable' ? (
    <ConfirmModal
      cancelText={<FormattedMessage id="state.cancel" />}
      confirmText={<FormattedMessage id="menu-item.enable" />}
      description={<FormattedMessage id="menu.bulkActions.statusChange.enableItemsDescription" />}
      header={
        <BulkActionModalTitle
          count={selectedIds.size}
          title={<FormattedMessage id="menu.bulkActions.statusChange.enableItems" />}
        />
      }
      loading={handleStatusChange.isLoading}
      onCancel={onClose}
      onConfirm={() => {
        handleStatusChange.mutate({
          ...buildBulkActionBasePayload(categories, selectedIds),
          enabled_flag: true,
        });
      }}
      open
    />
  ) : (
    <ConfirmModal
      cancelText={<FormattedMessage id="state.cancel" />}
      confirmText={<FormattedMessage id="menu-item.disable" />}
      confirmVariant="secondaryNegative"
      description={<FormattedMessage id="menu.bulkActions.statusChange.disableItemsDescription" />}
      header={
        <BulkActionModalTitle
          count={selectedIds.size}
          title={<FormattedMessage id="menu.bulkActions.statusChange.disableItems" />}
        />
      }
      loading={handleStatusChange.isLoading}
      onCancel={onClose}
      onConfirm={() => {
        handleStatusChange.mutate({
          ...buildBulkActionBasePayload(categories, selectedIds),
          enabled_flag: false,
        });
      }}
      open
    />
  );
};

export default BulkStatusChangeModal;
